import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, comicCategories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const category = comicCategories.find((c) => c.slug === slug);
  const categoryName = category?.name ?? '全部漫画';

  const filteredComics = useMemo(() => {
    let list = slug ? comics.filter((c) => c.categorySlug === slug) : comics;
    if (activeTag) {
      list = list.filter((c) => c.tags.includes(activeTag));
    }
    return list;
  }, [slug, activeTag]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    comics
      .filter((c) => (slug ? c.categorySlug === slug : true))
      .forEach((c) => c.tags.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet);
  }, [slug]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 哔咔漫画网`,
    description: `哔咔漫画网${categoryName}分类，收录${filteredComics.length}部精彩漫画，无需注册免费在线阅读。`,
    url: slug ? `http://xiaodianba.cn/category/${slug}` : 'http://xiaodianba.cn/category',
    isPartOf: { '@type': 'WebSite', name: '哔咔漫画网', url: 'http://xiaodianba.cn/' },
    about: { '@type': 'Thing', name: `${categoryName}漫画` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://xiaodianba.cn/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: slug ? `http://xiaodianba.cn/category/${slug}` : 'http://xiaodianba.cn/category' },
    ],
  };

  return (
    <>
      <title>{`${categoryName}漫画 - 哔咔漫画网 - 免费漫画大全`}</title>
      <meta name="description" content={`哔咔漫画网${categoryName}分类，收录${filteredComics.length}部精彩漫画，涵盖${allTags.join('、')}等题材，无需注册免费在线阅读。`} />
      <meta name="keywords" content={`${categoryName},漫画,免费漫画,在线阅读,哔咔漫画网`} />
      <link rel="canonical" href={slug ? `http://xiaodianba.cn/category/${slug}` : 'http://xiaodianba.cn/category'} />
      <meta property="og:title" content={`${categoryName}漫画 - 哔咔漫画网`} />
      <meta property="og:description" content={`哔咔漫画网${categoryName}分类，收录${filteredComics.length}部精彩漫画，无需注册免费在线阅读。`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={slug ? `http://xiaodianba.cn/category/${slug}` : 'http://xiaodianba.cn/category'} />
      <meta property="og:site_name" content="哔咔漫画网" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${categoryName}漫画 - 哔咔漫画网`} />
      <meta name="twitter:description" content={`收录${filteredComics.length}部${categoryName}漫画，免费在线阅读。`} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: categoryName, path: slug ? `/category/${slug}` : '/category' },
              ]}
            />

            {/* Category switching tabs */}
            <div className="flex flex-wrap gap-2 mt-5 mb-4">
              {comicCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 mt-2">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  {categoryName}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  共 {filteredComics.length} 部作品
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeTag === tag
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-16">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-2.5 bg-background-100">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 哔咔漫画${categoryName}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2.5 left-2.5">
                        <span className="text-xs bg-accent-500 text-background-50 px-1.5 py-0.5 rounded-full">
                          {comic.rating}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <span className="text-xs text-background-50/80">{comic.updateChapter}</span>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm text-foreground-900 font-bold truncate group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 truncate">{comic.author}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画作品</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
